import React from 'react';

/**
 * Minimal markdown-lite renderer used across CMS pages.
 * Supports:
 *   ## Heading       → styled section heading
 *   - Bullet         → unordered list item (grouped into a <ul>)
 *   **bold**         → inline bold
 *   blank line       → paragraph break
 *
 * Purposely lightweight — no dependency on a full markdown lib.
 */
const inline = (text) => {
  // Split on **bold** while keeping tokens
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-[#0b1a2e]">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const RichContent = ({ text, className = '' }) => {
  if (!text) return null;

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let buffer = [];
  let inList = false;
  let listItems = [];

  const flushParagraph = () => {
    if (buffer.length) {
      blocks.push({ type: 'p', content: buffer.join(' ').trim() });
      buffer = [];
    }
  };
  const flushList = () => {
    if (listItems.length) {
      blocks.push({ type: 'ul', items: [...listItems] });
      listItems = [];
    }
    inList = false;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    const headingMatch = line.match(/^##\s+(.*)$/);
    const bulletMatch = line.match(/^-\s+(.*)$/);

    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'h', content: headingMatch[1] });
      continue;
    }
    if (bulletMatch) {
      flushParagraph();
      inList = true;
      listItems.push(bulletMatch[1]);
      continue;
    }
    if (inList) {
      // Continuation of previous bullet
      listItems[listItems.length - 1] += ' ' + line.trim();
    } else {
      buffer.push(line.trim());
    }
  }
  flushParagraph();
  flushList();

  return (
    <div className={`space-y-5 ${className}`} data-testid="rich-content">
      {blocks.map((b, i) => {
        if (b.type === 'h') {
          return (
            <h3
              key={i}
              className="pt-4 first:pt-0 text-xl lg:text-2xl text-[#0b1a2e] border-l-4 border-[#0f5c3d] pl-4"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              {b.content}
            </h3>
          );
        }
        if (b.type === 'p') {
          return (
            <p
              key={i}
              className="text-[#0b1a2e]/80 leading-relaxed text-[15px]"
            >
              {inline(b.content)}
            </p>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul key={i} className="space-y-2 pl-1">
              {b.items.map((it, j) => (
                <li key={j} className="flex gap-3 text-[#0b1a2e]/80 text-[15px] leading-relaxed">
                  <span
                    aria-hidden
                    className="mt-2 w-1.5 h-1.5 rounded-full bg-[#0f5c3d] shrink-0"
                  />
                  <span>{inline(it)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </div>
  );
};

export default RichContent;
